/**
 * #648 — what a message sent while a turn is running does. ONE rule for every chat surface (the workspace chat
 * `ChatPanel` and the chat home / mobile `HeroChat`), so they can't drift:
 *
 *  1. A prompt card is open (permission or question) → the message SKIPS it (owner, 2026-10-22): the server suspends
 *     the turn's parked waits and waits for the turn to end (`skipOpenPrompts`). Ended → send the message as an
 *     ordinary prompt, which supersedes the skipped requests. The skip landed but the turn is still finishing → never
 *     steer into it (it is ending: the steer would go unanswered); the text comes back with a notice.
 *  2. Otherwise (no card, or nothing was parked — a stale card) → steer, as before #648. `not_running` means there is
 *     no run (it ended, or a worker restart suspended it while the client still showed it live — #540): send as an
 *     ordinary prompt now. Every other refusal is transient: the text comes back, with a notice (a toast, never the
 *     error banner, whose only controls abort the run).
 */
import type { SteerResult } from './hooks.js'
import { currentSessionSignal, sessionEndedSince } from '@papan/api-client'

export type SkipOutcome = 'ended' | 'pending' | 'failed'

export type MidRunSend =
  /** delivered into the running turn — nothing more to do */
  | { kind: 'steered' }
  /** no run to steer into (it ended, or the skip ended it): send the text as an ordinary prompt now */
  | { kind: 'send' }
  /** not delivered: put the text back in the composer and show `notice` (null = say nothing) */
  | { kind: 'restore'; notice: string | null }

export async function sendMidRun(input: {
  text: string
  /** a permission or question card is open on this session */
  hasOpenCards: boolean
  skip: () => Promise<SkipOutcome>
  steer: (text: string) => Promise<SteerResult>
  describeRefusal: (reason: Extract<SteerResult, { ok: false }>['reason']) => string | null
  stillFinishingNotice: string
}): Promise<MidRunSend> {
  // #1316 review round 1 (B-2): the flow belongs to the session it started in — after that session ended (an identity
  // switch while the skip is held, ~15 s), nothing is steered or sent as the NEXT one: the text comes back, quietly
  // (same person; an account end reloads the page)
  const start = currentSessionSignal()
  const ended = (): MidRunSend | null => (sessionEndedSince(start) ? { kind: 'restore', notice: null } : null)
  const skip = input.hasOpenCards ? await input.skip() : 'failed'
  const endedAfterSkip = ended()
  if (endedAfterSkip) return endedAfterSkip
  if (skip === 'pending') return { kind: 'restore', notice: input.stillFinishingNotice }
  if (skip === 'ended') return { kind: 'send' }
  const steered = await input.steer(input.text)
  if (steered.ok) return { kind: 'steered' }
  const endedAfterSteer = ended()
  if (endedAfterSteer) return endedAfterSteer
  if (steered.reason === 'not_running') return { kind: 'send' }
  return { kind: 'restore', notice: input.describeRefusal(steered.reason) }
}
